import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  PieChart, Pie, Cell, LineChart, Line, Legend,
} from "recharts";
import {
  Sparkles, PhoneCall, FileSearch, RefreshCw, ShieldCheck, BellRing,
  TrendingUp, Wallet, PiggyBank, X,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtCurrency, fmtDate } from "@/lib/format";
import { PageHeader } from "@/components/CrudModule";

const CHART_COLORS = ["#17324D", "#315A7D", "#B28A3D", "#5B8AB0", "#8FAFC9", "#C9A95E", "#6E8BA3"];

function Kpi({ icon: Icon, label, value, tone = "blue", testid }) {
  const tones = {
    blue: "bg-[#315A7D]/10 text-[#315A7D]",
    amber: "bg-amber-50 text-amber-600",
    violet: "bg-[#B28A3D]/12 text-[#8a6a2e]",
    red: "bg-red-50 text-red-600",
    emerald: "bg-emerald-50 text-emerald-600",
    green: "bg-green-50 text-green-600",
  };
  return (
    <div className="rounded-lg border border-border bg-card p-4 transition-shadow hover:shadow-sm" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="overline">{label}</div>
        <div className={`grid h-8 w-8 place-items-center rounded-md ${tones[tone]}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <div className="mt-2 font-heading text-2xl font-bold tracking-tight tabular-nums">{value}</div>
    </div>
  );
}

function ChartCard({ title, children, className = "" }) {
  return (
    <div className={`rounded-lg border border-border bg-card p-4 ${className}`}>
      <h3 className="font-heading text-sm font-semibold mb-3">{title}</h3>
      <div className="h-64">{children}</div>
    </div>
  );
}

const tooltipStyle = {
  contentStyle: { background: "#020817", border: "none", borderRadius: 8, fontSize: 12, color: "#fff" },
  labelStyle: { color: "#cbd5e1" },
  itemStyle: { color: "#fff" },
};

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [savedViews, setSavedViews] = useState([]);

  const loadViews = () => api.get("/views").then(({ data }) => setSavedViews(data)).catch(() => {});
  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setData(data));
    loadViews();
  }, []);

  if (!data) return <div className="grid place-items-center h-64 text-muted-foreground">Loading dashboard…</div>;
  const { cards, charts, views } = data;
  const showComm = cards.show_commission;
  const today = new Date().toISOString().slice(0, 10);

  const SHORTCUTS = [
    { label: "Assigned to Me", count: views.assigned_to_me, to: `/pipeline?owner=${encodeURIComponent(user.name)}`, tone: "blue" },
    { label: "Andis Today", count: views.andis_today, to: `/pipeline?due=${today}`, tone: "blue" },
    { label: "BPO Intake Needed", count: views.bpo_intake_needed, to: "/pipeline?stage=New Lead", tone: "amber" },
    { label: "Pending Underwriting", count: views.pending_underwriting, to: "/pipeline?stage=Pending Underwriting", tone: "violet" },
    { label: "Requote Today", count: views.requote_today, to: "/requote", tone: "red" },
    { label: "Follow-Ups Due", count: views.followups_due, to: "/followups", tone: "amber" },
    { label: "Commission Missing", count: views.commission_missing, to: "/commissions", tone: "red", commOnly: true },
    { label: "Policies Issued This Month", count: views.policies_this_month, to: "/policies", tone: "emerald" },
  ].filter((s) => !s.commOnly || showComm);

  const toneBar = {
    blue: "bg-[#315A7D]", amber: "bg-[#B28A3D]", violet: "bg-[#17324D]",
    red: "bg-red-500", emerald: "bg-emerald-500",
  };

  const openSavedView = (v) => {
    const qs = new URLSearchParams(v.filters || {}).toString();
    navigate(`${v.route}${qs ? `?${qs}` : ""}`);
  };
  const removeView = async (e, id) => {
    e.stopPropagation();
    await api.delete(`/views/${id}`).catch(() => {});
    loadViews();
  };

  return (
    <div data-testid="dashboard-page">
      <PageHeader title={`Welcome back, ${user.name.split(" ")[0]}`} subtitle="Live operations overview across your pipeline." />

      {/* Saved operational views */}
      <div className="mb-6">
        <div className="overline mb-2">Saved Views & Shortcuts</div>
        <div className="flex flex-wrap gap-2">
          {SHORTCUTS.map((s) => (
            <button
              key={s.label}
              onClick={() => navigate(s.to)}
              data-testid={`view-shortcut-${s.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
              className="group flex items-center gap-2.5 rounded-full border border-border bg-card py-1.5 pl-2.5 pr-3.5 text-sm transition-colors hover:border-primary hover:bg-secondary"
            >
              <span className={`h-2 w-2 rounded-full ${toneBar[s.tone]}`} />
              <span className="font-medium">{s.label}</span>
              <span className="rounded-full bg-secondary px-2 py-0.5 text-xs font-semibold tabular-nums text-foreground group-hover:bg-card">{s.count}</span>
            </button>
          ))}
          {savedViews.map((v) => (
            <button
              key={v.id}
              onClick={() => openSavedView(v)}
              data-testid={`saved-view-${v.id}`}
              className="group flex items-center gap-2 rounded-full border border-primary/40 bg-primary/5 py-1.5 pl-3 pr-2.5 text-sm text-primary transition-colors hover:bg-primary/10"
            >
              <span className="font-medium">★ {v.name}</span>
              <span onClick={(e) => removeView(e, v.id)} className="rounded-full p-0.5 hover:bg-primary/20" title="Remove">
                <X className="h-3.5 w-3.5" />
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-3 gap-4">
        <Kpi icon={Sparkles} label="New Leads" value={cards.new_leads} tone="blue" testid="kpi-new-leads" />
        <Kpi icon={PhoneCall} label="Leads Needing Contact" value={cards.needs_contact} tone="amber" testid="kpi-needs-contact" />
        <Kpi icon={FileSearch} label="Pending Underwriting" value={cards.pending_uw} tone="violet" testid="kpi-pending-uw" />
        <Kpi icon={RefreshCw} label="Requotes Needed" value={cards.requotes_needed} tone="red" testid="kpi-requotes" />
        <Kpi icon={ShieldCheck} label="Policies Issued" value={cards.policies_issued} tone="emerald" testid="kpi-policies" />
        <Kpi icon={BellRing} label="Follow-Ups Due Today" value={cards.followups_due_today} tone="amber" testid="kpi-followups" />
        {showComm && <Kpi icon={TrendingUp} label="Expected Commission" value={fmtCurrency(cards.expected_commission)} tone="blue" testid="kpi-expected" />}
        {showComm && <Kpi icon={Wallet} label="Commission Received" value={fmtCurrency(cards.commission_received)} tone="green" testid="kpi-received" />}
        {showComm && <Kpi icon={PiggyBank} label="Net Commission After Lead Cost" value={fmtCurrency(cards.net_commission)} tone="emerald" testid="kpi-net" />}
      </div>

      {/* Charts */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Lead Count by Stage" className="lg:col-span-2">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={charts.leads_by_stage} margin={{ top: 4, right: 8, left: -16, bottom: 40 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
              <XAxis dataKey="stage" tick={{ fontSize: 10 }} angle={-35} textAnchor="end" interval={0} height={60} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip {...tooltipStyle} />
              <Bar dataKey="count" fill="#315A7D" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Pending Cases by Owner">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={charts.pending_by_owner} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" horizontal={false} />
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="owner" tick={{ fontSize: 11 }} width={90} />
              <Tooltip {...tooltipStyle} />
              <Bar dataKey="count" fill="#17324D" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Policies Issued by Carrier">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={charts.policies_by_carrier} dataKey="count" nameKey="carrier" cx="50%" cy="50%" outerRadius={80} label={{ fontSize: 10 }}>
                {charts.policies_by_carrier.map((e, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Pie>
              <Tooltip {...tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        {showComm && (
          <ChartCard title="Expected vs Received Commission">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={charts.exp_vs_rec} margin={{ left: -16, bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                <XAxis dataKey="carrier" tick={{ fontSize: 9 }} angle={-30} textAnchor="end" interval={0} height={50} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip {...tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="expected" fill="#315A7D" radius={[4, 4, 0, 0]} />
                <Bar dataKey="received" fill="#059669" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        )}

        <ChartCard title="Follow-Ups Due by Date">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={charts.followups_by_date.map((d) => ({ ...d, label: fmtDate(d.date) }))} margin={{ left: -20, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip {...tooltipStyle} />
              <Line type="monotone" dataKey="count" stroke="#D97706" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Approval & Decline Trends">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={charts.approval_trends} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={80} label={{ fontSize: 10 }}>
                {charts.approval_trends.map((e, i) => <Cell key={i} fill={["#059669", "#DC2626", "#D97706"][i]} />)}
              </Pie>
              <Tooltip {...tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        {showComm && (
          <ChartCard title="Lead Source Profitability (Net)" className="lg:col-span-2">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={charts.lead_source_profitability} margin={{ left: -10, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                <XAxis dataKey="source" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip {...tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="cost" name="Lead Cost" fill="#DC2626" radius={[4, 4, 0, 0]} />
                <Bar dataKey="commission" name="Commission" fill="#315A7D" radius={[4, 4, 0, 0]} />
                <Bar dataKey="net" name="Net" fill="#059669" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        )}
      </div>
    </div>
  );
}
